/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { number } from 'libs/number';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Section } from 'ui/molecules/Section/Section';

import styles from './BusinessCreditRiskFile.module.scss';

import {
  DBT_VALUE,
  DIRECTORS,
  FINANCIALS_YEARS,
  getAgingBuckets,
  getClock,
  getDbtStats,
  getFileIndex,
  getFinancialsMeta,
  getFinancialsRows,
  getGroupRollup,
  getIdentityFields,
  getLimitRows,
  getScoreBand,
  GROUP_TIERS,
  JUDGMENTS,
  LIMIT_VALUE,
  SCORE_HISTORY,
  SCORE_VALUE,
  SECURITY,
  SHARE_REGISTER,
  UBO,
} from './BusinessCreditRiskFile.data';

type Tone = 'green' | 'amber' | 'red' | 'blue' | 'neutral';

const StatusPill = ({ tone, children }: { tone: Tone; children: ReactNode }) => (
  <span className={cn(styles.pill, styles[`pill${tone}`])}>
    <i />
    {children}
  </span>
);

const BlockHeader = ({ n, title, note }: { n: string; title: string; note: string }) => (
  <div className={styles.blockHead}>
    <h3 className={styles.blockTitle}>
      <span>{n}</span>
      {title}
    </h3>
    <p className={styles.blockNote}>{note}</p>
  </div>
);

const useReveal = (threshold = 0.18) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { threshold });
  return { ref, inView };
};

const useCountTo = (target: number, active: boolean, duration = 1100) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);

  return value;
};

export const BusinessCreditRiskFile = () => {
  const { t } = useInt();
  const fileIndex = getFileIndex(t);
  const identityFields = getIdentityFields(t);
  const limitRows = getLimitRows(t);
  const dbtStats = getDbtStats(t);
  const agingBuckets = getAgingBuckets(t);
  const clock = getClock(t);
  const financialsRows = getFinancialsRows(t);
  const financialsMeta = getFinancialsMeta(t);
  const groupRollup = getGroupRollup(t);
  const scoreBand = getScoreBand(t);

  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>(fileIndex[0].id);

  useEffect(() => {
    const blocks = containerRef.current?.querySelectorAll<HTMLElement>('[data-block]');

    if (!blocks?.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );

    blocks.forEach((block) => observer.observe(block));

    return () => observer.disconnect();
  }, []);

  const scrollToBlock = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const identity = useReveal();
  const score = useReveal();
  const payment = useReveal();
  const judgments = useReveal();
  const security = useReveal();
  const financials = useReveal();
  const ownership = useReveal();
  const group = useReveal(0.12);

  const scoreValue = useCountTo(SCORE_VALUE, score.inView);
  const limitValue = useCountTo(LIMIT_VALUE, score.inView, 1200);
  const dbtValue = useCountTo(DBT_VALUE, payment.inView, 800);

  return (
    <Section
      className={styles.root}
      title={t('One company, read end to end')}
      description={t(
        'Eight blocks, each populated from the filing that produced it. This is the product view, not a summary of it.',
      )}
      align="left"
    >
      <div className={styles.layout}>
        <nav className={styles.index}>
          {fileIndex.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={cn(styles.indexLink, active === item.id && styles.indexLinkOn)}
              onClick={(event) => {
                event.preventDefault();
                scrollToBlock(item.id);
              }}
            >
              <span className={styles.indexN}>{item.n}</span>
              {item.label}
            </a>
          ))}
        </nav>

        <div className={styles.blocks} ref={containerRef}>
          <div id="b1" data-block className={cn(styles.block, identity.inView && styles.blockIn)} ref={identity.ref}>
            <BlockHeader n="01" title={t('Identity')} note={t('Resolved once, then everything else attaches to it.')} />
            <dl className={styles.kv}>
              {identityFields.map((field) => (
                <div key={field.k} className={cn(field.span && styles.kvSpan)}>
                  <dt>{field.k}</dt>
                  <dd className={cn(field.mono && styles.mono)}>
                    {field.tone ? <StatusPill tone={field.tone}>{field.v}</StatusPill> : field.v}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div id="b2" data-block className={cn(styles.block, score.inView && styles.blockIn)} ref={score.ref}>
            <BlockHeader
              n="02"
              title={t('Score and limit')}
              note={t('Recalculated when a filing lands, not on a fixed review cycle.')}
            />
            <div className={styles.scoreGrid}>
              <div>
                <div className={styles.scoreBig}>
                  {scoreValue}
                  <i>/100</i>
                </div>
                <div className={styles.scoreBand}>{scoreBand}</div>

                <div className={styles.scale}>
                  <span className={cn(styles.scaleZone, styles.scaleHigh)}>{t('1–40 HIGH')}</span>
                  <span className={cn(styles.scaleZone, styles.scaleMod)}>{t('41–70 MOD')}</span>
                  <span className={cn(styles.scaleZone, styles.scaleLow)}>{t('71–100 LOW')}</span>
                  <span className={styles.scaleMark} style={{ left: score.inView ? `${SCORE_VALUE}%` : '0%' }} />
                </div>

                <div className={styles.hist}>
                  {SCORE_HISTORY.map((value, i) => (
                    <i
                      key={i}
                      className={cn(i === SCORE_HISTORY.length - 1 && styles.histCur)}
                      style={{ height: score.inView ? `${value * 0.56}px` : 0 }}
                    />
                  ))}
                </div>
                <div className={styles.histLabels}>
                  <span>Q3 2023</span>
                  <span>{t('12 quarters')}</span>
                  <span>Q2 2026</span>
                </div>
              </div>

              <div className={styles.limBox}>
                <span className={styles.label}>{t('Recommended credit limit')}</span>
                <div className={styles.limValue}>&pound;{number(limitValue)}</div>
                <dl className={styles.limRows}>
                  {limitRows.map((row) => (
                    <div key={row.k}>
                      <dt>{row.k}</dt>
                      <dd>{row.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>

          <div id="b3" data-block className={cn(styles.block, payment.inView && styles.blockIn)} ref={payment.ref}>
            <BlockHeader
              n="03"
              title={t('Payment behaviour')}
              note={t('Contributed trade ledgers. A figure publishes only once enough independent lines exist.')}
            />
            <div className={styles.dbtGrid}>
              <div>
                <span className={styles.label}>{t('Average days beyond terms')}</span>
                <div className={styles.dbtBig}>
                  {dbtValue}
                  <em>{t('days')}</em>
                </div>
                <dl className={styles.limRows}>
                  {dbtStats.map((row) => (
                    <div key={row.k}>
                      <dt>{row.k}</dt>
                      <dd>{row.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className={styles.aging}>
                {agingBuckets.map((bucket, i) => (
                  <div className={styles.agingRow} key={bucket.label}>
                    <span className={styles.agingKey}>{bucket.label}</span>
                    <div className={styles.agingTrack}>
                      <span
                        className={cn(styles.agingFill, styles[`tone${bucket.tone}`])}
                        style={{ width: payment.inView ? `${bucket.pct}%` : 0, transitionDelay: `${i * 0.08}s` }}
                      />
                    </div>
                    <span className={styles.agingVal}>{bucket.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div id="b4" data-block className={cn(styles.block, judgments.inView && styles.blockIn)} ref={judgments.ref}>
            <BlockHeader
              n="04"
              title={t('Court judgments')}
              note={t('Registration date, amount, court, case number and satisfaction status held as separate fields.')}
            />
            <div className={styles.tableWrap}>
              <table className={cn(styles.table, styles.tableWide)}>
                <thead>
                  <tr>
                    <th>{t('Court')}</th>
                    <th>{t('Case')}</th>
                    <th>{t('Registered')}</th>
                    <th>{t('Status')}</th>
                    <th className={styles.right}>{t('Amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {JUDGMENTS.map((row) => (
                    <tr key={row.caseNo} className={cn(row.dim && styles.rowDim)}>
                      <td className={styles.strong}>
                        {row.court}
                        <span className={styles.sub}>{row.region}</span>
                      </td>
                      <td className={styles.mono}>{row.caseNo}</td>
                      <td className={styles.mono}>{row.registered}</td>
                      <td>
                        <StatusPill tone={row.tone}>{row.status}</StatusPill>
                      </td>
                      <td className={cn(styles.mono, styles.right)}>{row.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.footNote}>
              {t(
                'An entry runs six years from registration. Payment inside one month can remove it. Payment after that marks it satisfied and it stays visible for the remaining term. Source: Register of Judgments, Orders and Fines, maintained by Registry Trust for the Ministry of Justice.',
              )}
            </p>
          </div>

          <div id="b5" data-block className={cn(styles.block, security.inView && styles.blockIn)} ref={security.ref}>
            <BlockHeader
              n="05"
              title={t('Registered security')}
              note={t('UK charges and US financing statements on one view, resolved to the group.')}
            />
            <div className={styles.tableWrap}>
              <table className={cn(styles.table, styles.tableWide)}>
                <thead>
                  <tr>
                    <th>{t('Instrument')}</th>
                    <th>{t('Holder')}</th>
                    <th>{t('Collateral')}</th>
                    <th>{t('Filed')}</th>
                    <th className={styles.right}>{t('Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {SECURITY.map((row) => (
                    <tr key={row.ref} className={cn(row.dim && styles.rowDim)}>
                      <td className={styles.mono}>
                        {row.instrument}
                        <span className={styles.sub}>{row.ref}</span>
                      </td>
                      <td className={styles.strong}>
                        {row.holder}
                        {row.debtor && <span className={styles.sub}>{row.debtor}</span>}
                      </td>
                      <td>{row.collateral}</td>
                      <td className={styles.mono}>{row.filed}</td>
                      <td className={styles.right}>
                        <StatusPill tone={row.tone}>{row.status}</StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.clock}>
              <span className={styles.label}>{clock.label}</span>
              <div className={styles.clockTrack}>
                <span className={styles.clockWindow} style={{ width: `${clock.windowPct}%` }} />
                <span className={styles.clockFill} style={{ width: security.inView ? `${clock.fillPct}%` : 0 }} />
              </div>
              <div className={styles.clockLabels}>
                <span>{clock.filed}</span>
                <span>{clock.windowOpen}</span>
                <span>{clock.lapses}</span>
              </div>
              <p className={styles.footNote}>
                {t(
                  'A financing statement runs five years from filing. A UCC-3 continuation is effective only inside the six months immediately before the lapse date, so a filing sitting on the record can already be unperfected. We alert on the window opening rather than the lapse.',
                )}
              </p>
            </div>
          </div>

          <div
            id="b6"
            data-block
            className={cn(styles.block, financials.inView && styles.blockIn)}
            ref={financials.ref}
          >
            <BlockHeader
              n="06"
              title={t('Financials')}
              note={t('Filed figures as filed. Anything calculated is labelled as calculated.')}
            />
            <div className={styles.tableWrap}>
              <table className={cn(styles.table, styles.finTable)}>
                <thead>
                  <tr>
                    <th />
                    {FINANCIALS_YEARS.map((year, i) => (
                      <th key={year} className={cn(styles.right, i === FINANCIALS_YEARS.length - 1 && styles.finCur)}>
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {financialsRows.map((row) => (
                    <tr key={row.label}>
                      <td className={styles.strong}>
                        {row.label}
                        {row.calculated && <span className={styles.calcTag}>{t('calculated')}</span>}
                      </td>
                      {row.values.map((value, i) => (
                        <td
                          key={i}
                          className={cn(styles.mono, styles.right, i === row.values.length - 1 && styles.finCur)}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className={styles.kv} style={{ marginTop: '1.6rem' }}>
              {financialsMeta.map((field) => (
                <div key={field.k}>
                  <dt>{field.k}</dt>
                  <dd className={styles.mono}>{field.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div id="b7" data-block className={cn(styles.block, ownership.inView && styles.blockIn)} ref={ownership.ref}>
            <BlockHeader
              n="07"
              title={t('Ownership')}
              note={t('Directors, the share register, and the person behind the holding company.')}
            />
            <div className={styles.own2}>
              <div>
                <span className={styles.label}>{t('Directors and officers')}</span>
                <div className={styles.tableWrap}>
                  <table className={styles.table} style={{ marginTop: '0.9rem' }}>
                    <thead>
                      <tr>
                        <th>{t('Name')}</th>
                        <th>{t('Appointed')}</th>
                        <th className={styles.right}>{t('Other roles')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DIRECTORS.map((director) => (
                        <tr key={director.name} className={cn(director.dim && styles.rowDim)}>
                          <td className={styles.strong}>
                            {director.name}
                            <span className={styles.sub}>{director.role}</span>
                          </td>
                          <td className={styles.mono}>{director.appointed}</td>
                          <td className={cn(styles.mono, styles.right)}>{director.roles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <span className={styles.label}>{t('Share register, as at')} 31 Mar 2026</span>
                <div className={styles.tableWrap}>
                  <table className={styles.table} style={{ marginTop: '0.9rem' }}>
                    <thead>
                      <tr>
                        <th>{t('Holder')}</th>
                        <th>{t('Class')}</th>
                        <th className={styles.right}>{t('Holding')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SHARE_REGISTER.map((row) => (
                        <tr key={row.holder}>
                          <td className={styles.strong}>
                            {row.holder}
                            {row.sub && <span className={styles.sub}>{row.sub}</span>}
                          </td>
                          <td className={styles.mono}>{row.cls}</td>
                          <td className={cn(styles.mono, styles.right)}>
                            {row.pct}%
                            <div className={styles.pctTrack}>
                              <span
                                className={styles.pctFill}
                                style={{ width: ownership.inView ? `${row.pct}%` : 0 }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className={styles.ubo}>
                  <b>{UBO.name}</b>
                  <p>{UBO.detail}</p>
                </div>
              </div>
            </div>
          </div>

          <div id="b8" data-block className={cn(styles.block, group.inView && styles.blockIn)} ref={group.ref}>
            <BlockHeader
              n="08"
              title={t('Group structure')}
              note={t('Three limits of £1.5M across one group is not three decisions.')}
            />

            <div className={cn(styles.tree, group.inView && styles.treeIn)}>
              {GROUP_TIERS.map((tier, tierIndex) => (
                <div className={styles.treeTier} key={tierIndex}>
                  {tier.map((node) => (
                    <div className={cn(styles.treeNode, node.highlight && styles.treeNodeHi)} key={node.name}>
                      <b>{node.name}</b>
                      <span>
                        {node.reg} &middot; {t('score')}{' '}
                        <em className={node.score >= 70 ? styles.scoreGood : styles.scoreWeak}>{node.score}</em>
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className={styles.roll}>
              {groupRollup.map((item) => (
                <div key={item.label}>
                  <b>{item.v}</b>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
